/**
 * FlahaSOIL v2 — chart snapshot capture (Phase 10E+).
 *
 * Rasterises chart panels to opaque PNG data URLs for the PDF.
 *
 * Resilience:
 *   - print-capture mode (high-contrast theme, hide notes)
 *   - prefer `[data-chart-core]` so figures are not crushed by chrome
 *   - size-gate each PNG (server max is 1.5 MB decoded) — re-try at
 *     lower density or drop the slot so one huge image never kills the
 *     whole download with a NetworkError / body-limit failure
 */
import { toPng } from "html-to-image";
import type { ChartSnapshotKey, ChartSnapshotsDTO } from "@flaha/shared-types";

import { PRINT_CAPTURE_EVENT } from "../charts/utils/useChartTheme";

const CHART_KEYS: readonly ChartSnapshotKey[] = [
	"textureTriangle",
	"waterRetention",
	"baseSaturation",
	"cationBalance",
];

/** Primary capture density — sharp on A4 without huge payloads. */
const PIXEL_RATIO_PRIMARY = 2;
/** Fallback density when the first capture exceeds the size budget. */
const PIXEL_RATIO_FALLBACK = 1.25;

/**
 * Keep well under the server's 1.5 MB decoded cap so JSON base64 + 4
 * charts stay inside the 8 MB route body limit.
 */
const MAX_DATA_URL_CHARS = 1_800_000; // ~1.3 MB decoded

const PRINT_SETTLE_MS = 120;

function sleep(ms: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

async function enterPrintCaptureMode(): Promise<void> {
	document.documentElement.dataset.printCapture = "true";
	window.dispatchEvent(new Event(PRINT_CAPTURE_EVENT));
	await new Promise<void>((r) =>
		requestAnimationFrame(() => requestAnimationFrame(() => r()))
	);
	await sleep(PRINT_SETTLE_MS);
}

async function exitPrintCaptureMode(): Promise<void> {
	delete document.documentElement.dataset.printCapture;
	window.dispatchEvent(new Event(PRINT_CAPTURE_EVENT));
	await new Promise<void>((r) => requestAnimationFrame(() => r()));
}

function captureTarget(panel: HTMLElement): HTMLElement {
	const core = panel.querySelector<HTMLElement>("[data-chart-core]");
	return core ?? panel;
}

function printFilter(node: HTMLElement): boolean {
	if (node.hasAttribute?.("data-print-hide")) return false;
	if (node.classList?.contains("MuiCollapse-hidden")) return false;
	return true;
}

function isAcceptablePng(dataUrl: string): boolean {
	if (!dataUrl.startsWith("data:image/png;base64,")) return false;
	const b64 = dataUrl.slice("data:image/png;base64,".length);
	// Reject empty / absurdly large payloads before they hit the wire.
	if (b64.length === 0) return false;
	if (dataUrl.length > MAX_DATA_URL_CHARS) return false;
	return true;
}

async function rasterise(
	target: HTMLElement,
	pixelRatio: number
): Promise<string | null> {
	try {
		const dataUrl = await toPng(target, {
			pixelRatio,
			backgroundColor: "#ffffff",
			cacheBust: true,
			// Prefer CORS-safe; ignore webfont failures that can hang capture.
			skipFonts: true,
			filter: (n) => printFilter(n as HTMLElement),
		});
		return isAcceptablePng(dataUrl) ? dataUrl : null;
	} catch {
		return null;
	}
}

/**
 * Capture every chart panel present under `root`. Oversized or failed
 * captures are omitted (PDF falls back to summary cards for those slots).
 */
export async function captureChartSnapshots(
	root: ParentNode = document
): Promise<ChartSnapshotsDTO> {
	const result: ChartSnapshotsDTO = {};
	const panels = CHART_KEYS.map((key) => ({
		key,
		node: root.querySelector<HTMLElement>(`[data-chart-snapshot="${key}"]`),
	})).filter(
		(p): p is { key: ChartSnapshotKey; node: HTMLElement } => p.node != null
	);

	if (panels.length === 0) return result;

	try {
		await enterPrintCaptureMode();

		// Sequential capture reduces peak memory (parallel 4× 2× PNG can
		// stall the tab and surface as NetworkError on the following POST).
		for (const { key, node } of panels) {
			const target = captureTarget(node);
			let dataUrl = await rasterise(target, PIXEL_RATIO_PRIMARY);
			if (!dataUrl) {
				dataUrl = await rasterise(target, PIXEL_RATIO_FALLBACK);
			}
			if (dataUrl) {
				result[key] = dataUrl;
			}
		}
	} finally {
		await exitPrintCaptureMode();
	}

	return result;
}

/** Exported for unit tests. */
export const __captureInternals = {
	MAX_DATA_URL_CHARS,
	PIXEL_RATIO_PRIMARY,
	PIXEL_RATIO_FALLBACK,
	isAcceptablePng,
};
